import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Panel, TactLoader } from "../components/Tactical";
import { useT } from "../context/LanguageContext";
import { toast } from "sonner";
import { MessageSquareOff, Trophy, AlertCircle, Wand2, RefreshCw, MessageSquare } from "lucide-react";
import NrChatModal from "../components/NrChatModal";

/**
 * Admin "Non-Responding Players" tab.
 * Lists matches that ended in a 3-0 chat-silence forfeit because one player
 * messaged on match day and the opponent never replied. Two sub-lists:
 *   • Live forfeits (auto-resolved by sweeper)
 *   • Backfill corrections (originally a 0-0 draw, corrected by admin tool)
 */
export default function NonRespondingTab() {
  const { t } = useT();
  const [data, setData] = useState({ live: [], backfilled: [], live_count: 0, backfilled_count: 0 });
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("live");
  const [chatRow, setChatRow] = useState(null);  // row currently shown in chat modal

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/non-responding?limit=300");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  if (loading) return <TactLoader label={t("nr.loading")} />;

  const rows = section === "live" ? data.live : data.backfilled;

  return (
    <div className="space-y-6" data-testid="non-responding-tab">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-1">{t("nr.kicker")}</div>
        <h2 className="font-heading text-3xl uppercase tracking-wider mb-1">{t("nr.title")}</h2>
        <p className="text-sm text-[#71717A] font-body max-w-2xl">{t("nr.subtitle")}</p>
      </div>

      {/* Section switch */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="nr-section-switch">
        <button
          type="button"
          onClick={() => setSection("live")}
          data-testid="nr-section-live"
          className={`inline-flex items-center gap-2 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] border transition-colors ${
            section === "live"
              ? "border-[#00FF87] text-[#00FF87] bg-[#00FF87]/10"
              : "border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          }`}
        >
          <MessageSquareOff className="w-3 h-3" /> {t("nr.section.live")} <span className="font-score text-sm">{data.live_count}</span>
        </button>
        <button
          type="button"
          onClick={() => setSection("backfilled")}
          data-testid="nr-section-backfilled"
          className={`inline-flex items-center gap-2 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] border transition-colors ${
            section === "backfilled"
              ? "border-[#EA580C] text-[#EA580C] bg-[#EA580C]/10"
              : "border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          }`}
        >
          <Wand2 className="w-3 h-3" /> {t("nr.section.backfilled")} <span className="font-score text-sm">{data.backfilled_count}</span>
        </button>
        <button
          type="button"
          onClick={refresh}
          data-testid="nr-refresh"
          className="ml-auto inline-flex items-center gap-2 border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors"
        >
          <RefreshCw className="w-3 h-3" /> {t("nr.refresh")}
        </button>
      </div>

      {/* Table */}
      {rows.length === 0 ? (
        <Panel testId={`nr-empty-${section}`} className="font-mono text-sm text-[#71717A]">
          {section === "live" ? t("nr.empty.live") : t("nr.empty.backfilled")}
        </Panel>
      ) : (
        <Panel testId={`nr-list-${section}`} className="overflow-x-auto p-0">
          <table className="w-full text-sm" data-testid={`nr-table-${section}`}>
            <thead className="bg-[#F4F4F5] dark:bg-[#0E0E14]">
              <tr className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
                <th className="text-start px-4 py-3">{t("nr.col.date")}</th>
                <th className="text-start px-4 py-3">{t("nr.col.league")}</th>
                <th className="text-start px-4 py-3">{t("nr.col.winner")}</th>
                <th className="text-start px-4 py-3">{t("nr.col.loser")}</th>
                <th className="text-start px-4 py-3">{t("nr.col.score")}</th>
                {section === "backfilled" && (
                  <th className="text-start px-4 py-3">{t("nr.col.original")}</th>
                )}
                <th className="text-start px-4 py-3">{t("nr.col.action")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <NrRow key={r.match_id} row={r} section={section} odd={i % 2 === 1} onViewChat={() => setChatRow(r)} />
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {chatRow && (
        <NrChatModal row={chatRow} onClose={() => setChatRow(null)} />
      )}
    </div>
  );
}

function NrRow({ row, section, odd, onViewChat }) {
  const { t } = useT();
  const date = row.scheduled_at
    ? new Date(row.scheduled_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "—";
  const score = `${row.final_score_home ?? "?"}-${row.final_score_away ?? "?"}`;
  const winnerName = row.winner?.username || t("nr.unknownPlayer");
  const loserName = row.loser?.username || t("nr.unknownPlayer");
  return (
    <tr
      className={`border-t border-[#E4E4E7] dark:border-[#2A2A30] font-body ${odd ? "bg-[#FAFAFC]/40 dark:bg-[#0A0A0F]/40" : ""}`}
      data-testid={`nr-row-${row.match_id}`}
    >
      <td className="px-4 py-3 font-mono text-[11px] text-[#71717A]">{date}</td>
      <td className="px-4 py-3">
        {row.league_id ? (
          <Link
            to={`/leagues/${row.league_id}`}
            className="text-[#7C3AED] hover:underline"
            data-testid={`nr-league-${row.match_id}`}
          >
            {row.league_name || row.league_id.slice(0, 8)}
          </Link>
        ) : (
          <span className="text-[#71717A]">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5">
          <Trophy className="w-3.5 h-3.5 text-[#00FF87]" />
          {row.winner ? (
            <Link
              to={`/players/${encodeURIComponent(winnerName)}`}
              className="text-[#09090B] dark:text-[#F4F4F5] hover:text-[#00FF87] font-bold"
              data-testid={`nr-winner-${row.match_id}`}
            >
              {winnerName}
            </Link>
          ) : (
            <span className="text-[#71717A]">{winnerName}</span>
          )}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 text-[#E11D48]" />
          {row.loser ? (
            <Link
              to={`/players/${encodeURIComponent(loserName)}`}
              className="text-[#09090B] dark:text-[#F4F4F5] hover:text-[#E11D48] line-through decoration-[#E11D48]/40"
              data-testid={`nr-loser-${row.match_id}`}
            >
              {loserName}
            </Link>
          ) : (
            <span className="text-[#71717A]">{loserName}</span>
          )}
        </span>
      </td>
      <td className="px-4 py-3 font-score text-base text-[#00FF87]" data-testid={`nr-score-${row.match_id}`}>
        {score}
      </td>
      {section === "backfilled" && (
        <td className="px-4 py-3 font-mono text-[11px]">
          <span className="line-through text-[#71717A]">{row.original_score || "0-0"}</span>
          <span className="mx-1 text-[#EA580C]">→</span>
          <span className="text-[#00FF87]">{score}</span>
        </td>
      )}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onViewChat}
            disabled={!row.winner?.id || !row.loser?.id}
            data-testid={`nr-view-chat-${row.match_id}`}
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] border border-[#E4E4E7] dark:border-[#2A2A30] px-2.5 py-1 hover:border-[#7C3AED] hover:text-[#7C3AED] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <MessageSquare className="w-3 h-3" /> {t("nr.col.viewChat")}
          </button>
          <Link
            to={`/matches/${row.match_id}`}
            className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-1 hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors"
            data-testid={`nr-view-${row.match_id}`}
          >
            {t("nr.col.view")}
          </Link>
        </div>
      </td>
    </tr>
  );
}
